import { logger } from '#~/logger/index.js';
import { Intercom, trackEvent } from '@intercom/messenger-js-sdk';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import type { IntercomEvent, IntercomEventMetadata } from './types.js';

type IntercomInstance = typeof window extends undefined
  ? undefined
  : (Window & typeof globalThis)['Intercom'];

type IntercomCompany = {
  id: string;
  name: string;
};

type SetupIntercomOptions = {
  email: string;
  name: string;
  userId: string;
  avatar?: T_Maybe<string>;
  companies?: T_Maybe<IntercomCompany[]>;
};

const vendorAppIdMap: { [key in Vendor]: string } = {
  [Vendor.IRIS]: 'cnnnlzuj',
  [Vendor.PTZOPTICS]: 'd0bljmph'
};

const INTERCOM_READY_CHECK_INTERVAL_MS = 250;

type PendingIntercomEvent = {
  eventName: string;
  metadata?: IntercomEventMetadata;
};
const createPendingEvent = (
  eventName: string,
  metadata?: IntercomEventMetadata
): PendingIntercomEvent => {
  return metadata === undefined ? { eventName } : { eventName, metadata };
};

let intercomReady = false;
let intercomReadyPromise: Promise<void> | null = null;
let resolveIntercomReady: (() => void) | null = null;
let readinessPollHandle: number | null = null;
let pendingEvents: PendingIntercomEvent[] = [];

const getIntercomInstance = (): IntercomInstance => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.Intercom;
};

const isPlaceholderIntercom = (instance: IntercomInstance): boolean => {
  return typeof instance === 'function' && typeof (instance as any).q !== 'undefined';
};

const isIntercomBooted = (): boolean => {
  const instance = getIntercomInstance();

  if (typeof instance !== 'function') {
    return false;
  }

  if (isPlaceholderIntercom(instance)) {
    return false;
  }

  try {
    // @ts-ignore
    return instance['booted'];
  } catch (error) {
    if (typeof logger.debug === 'function') {
      logger.debug('___Intercom boot check failed', { error });
    }
    return false;
  }
};

const clearReadinessPoll = () => {
  if (readinessPollHandle !== null && typeof window !== 'undefined') {
    window.clearTimeout(readinessPollHandle);
    readinessPollHandle = null;
  }
};

const flushPendingEvents = () => {
  if (!pendingEvents.length) {
    return;
  }

  const eventsToFlush = pendingEvents;
  pendingEvents = [];

  eventsToFlush.forEach(({ eventName, metadata }) => {
    try {
      trackEvent(eventName, metadata ?? {});
    } catch (error) {
      logger.error('Failed to flush Intercom event:', { error, eventName, metadata });
      pendingEvents.push(createPendingEvent(eventName, metadata));
    }
  });
};

const markIntercomReady = () => {
  if (intercomReady) {
    return;
  }

  intercomReady = true;
  clearReadinessPoll();
  resolveIntercomReady?.();
  resolveIntercomReady = null;
  intercomReadyPromise = null;

  flushPendingEvents();
};

const scheduleReadinessPoll = () => {
  if (typeof window === 'undefined' || intercomReady) {
    return;
  }

  if (readinessPollHandle !== null) {
    return;
  }

  const poll = () => {
    if (isIntercomBooted()) {
      markIntercomReady();
      return;
    }

    readinessPollHandle = window.setTimeout(poll, INTERCOM_READY_CHECK_INTERVAL_MS);
  };

  readinessPollHandle = window.setTimeout(poll, INTERCOM_READY_CHECK_INTERVAL_MS);
};

const waitForIntercomReadiness = (): Promise<void> => {
  if (intercomReady || typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!intercomReadyPromise) {
    intercomReadyPromise = new Promise((resolve) => {
      resolveIntercomReady = resolve;
    });
  }

  scheduleReadinessPoll();

  return intercomReadyPromise;
};

const queueIntercomEvent = (eventName: string, metadata?: IntercomEventMetadata) => {
  pendingEvents.push(createPendingEvent(eventName, metadata));

  scheduleReadinessPoll();
};

const normalizeEventArguments = (
  eventName: string,
  metadata?: IntercomEventMetadata | IntercomEvent
): PendingIntercomEvent => {
  if (metadata && typeof metadata === 'object' && 'event_name' in metadata) {
    const { event_name, metadata: innerMetadata, created_at } = metadata as IntercomEvent;

    return {
      eventName: event_name ?? eventName,
      metadata: {
        ...innerMetadata,
        ...(typeof created_at === 'number' ? { created_at } : {})
      }
    };
  }

  return createPendingEvent(eventName, metadata as IntercomEventMetadata | undefined);
};

const normalizeCompanies = (companies?: IntercomCompany[]): IntercomCompany[] | undefined => {
  if (!companies?.length) {
    return undefined;
  }

  return companies
    .filter((company) => company.id !== '')
    .map((company) => ({
      id: company.id,
      name: company.name || 'Unnamed Organization'
    }));
};

const buildPayload = ({ app_id, email, name, userId, avatar, companies }: any) => {
  const payload: Parameters<typeof Intercom>[0] = {
    app_id,
    user_id: userId,
    name,
    email
  };

  if (avatar) {
    payload.avatar = avatar;
  }

  const normalizedCompanies = normalizeCompanies(companies);
  if (normalizedCompanies) {
    payload.companies = normalizedCompanies as any;
  }

  return payload;
};

export const setupIntercom = ({
  email,
  name,
  userId,
  avatar,
  companies
}: SetupIntercomOptions): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isElectronTrayWindow()) {
    return;
  }

  const currentVendor = getCurrentVendor();
  const appId = vendorAppIdMap[currentVendor];

  if (!appId) {
    console.warn('___setupIntercom: missing app ID for vendor', currentVendor);
    return;
  }

  const payload = buildPayload({
    app_id: appId,
    email,
    name,
    userId,
    ...(avatar ? { avatar } : {}),
    ...(companies ? { companies } : {})
  });

  Intercom(payload);

  scheduleReadinessPoll();
  waitForIntercomReadiness()
    .then(() => {
      // logger.info('___Intercom ready for user', { userId });
    })
    .catch((error) => {
      logger.error('Intercom readiness wait failed', { error });
    });
};

/**
 * Tracks a custom event in Intercom.
 *
 * This function sends custom events to Intercom for user onboarding flows and analytics.
 * Errors are logged but do not block the user flow.
 *
 * @param eventName - The name of the event to track
 * @param metadata - Optional metadata to attach to the event
 *
 * @example
 * ```typescript
 * trackIntercomEvent('first-camera-added', {
 *   deviceId: '123',
 *   deviceType: 'onvif',
 *   roomId: '456'
 * });
 * ```
 */
export const trackIntercomEvent = (
  eventName: string,
  metadata?: IntercomEventMetadata | IntercomEvent
): void => {
  const { eventName: normalizedEventName, metadata: normalizedMetadata } = normalizeEventArguments(
    eventName,
    metadata
  );

  try {
    if (intercomReady) {
      trackEvent(normalizedEventName, normalizedMetadata ?? {});
      return;
    }

    queueIntercomEvent(normalizedEventName, normalizedMetadata);
  } catch (error) {
    const cause =
      typeof error === 'object' && error !== null && 'cause' in error
        ? (error as { cause?: unknown }).cause
        : undefined;

    logger.error('Failed to track Intercom event:', {
      error,
      cause,
      eventName: normalizedEventName,
      metadata: normalizedMetadata
    });
  }
};

export const __intercomTestHooks = {
  _isReady: () => intercomReady,
  _pendingEvents: () => [...pendingEvents],
  _markReady: markIntercomReady,
  _reset: () => {
    intercomReady = false;
    intercomReadyPromise = null;
    resolveIntercomReady = null;
    clearReadinessPoll();
    pendingEvents = [];
  }
};
